import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DOCUMENT_OWNER_LABELS, DOCUMENT_REFERENCE_LABELS, type DocumentOwnerKind, type DocumentReferenceKind, type DocumentTarget } from '@kontor/shared';
import { api, queryString } from '../../api';
import '../../components/person-picker/person-picker.css';

export interface PickedTarget {
  kind: DocumentOwnerKind | DocumentReferenceKind;
  id: string;
  label: string | null;
}

const KIND_LABELS: Record<string, string> = { ...DOCUMENT_OWNER_LABELS, ...DOCUMENT_REFERENCE_LABELS };

/** Adresse des Ziels in der Oberfläche, zum Verlinken in der Liste */
export function targetPath(kind: string, id: string): string {
  switch (kind) {
    case 'contact': return `/kontakte/${id}`;
    case 'debtor': return `/debitoren/${id}`;
    case 'creditor': return `/kreditoren/${id}`;
    case 'employee': return `/mitarbeiter/${id}`;
    case 'order': return `/auftraege/${id}`;
    case 'offer': return `/angebote/${id}`;
    case 'inquiry': return `/anfragen/${id}`;
    case 'task': return `/kalender/aufgaben/${id}`;
    case 'asset': return `/inventar/${id}`;
    case 'vehicle': return `/flotte/${id}`;
    default: return '/';
  }
}

/**
 * Suchfeld für „Gehört zu“ (Kontakte, Debitoren, Kreditoren, Mitarbeiter) und „Referenz“ (Aufträge, Angebote, Anfragen, Aufgaben). Ein Suchbegriff durchsucht alle
 * betroffenen Tabellen zugleich, die Treffer stehen nach Art gruppiert. Es erscheinen nur Ziele, die der Benutzer auch sonst sehen darf (das prüft der Server).
 * Das gewählte Ziel steht als Marke mit der Art davor und lässt sich entfernen.
 */
export function TargetPicker({ role, value, onChange, label, hint }: { role: 'owner' | 'reference'; value: PickedTarget | null; onChange: (t: PickedTarget | null) => void; label: string; hint?: string }) {
  const uid = useId();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // Erst nach einer kurzen Pause suchen, damit nicht jeder Tastendruck eine Abfrage auslöst
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const found = useQuery({
    queryKey: ['document-targets', role, debounced],
    queryFn: async () => (await api<{ data: DocumentTarget[] }>('GET', `/documents/targets${queryString({ role, q: debounced })}`)).data,
    enabled: open && debounced.length >= 2,
  });
  const results = found.data ?? [];

  if (value) {
    return (
      <div className="picker__chips">
        <span className={`picker__chip${value.label === null ? ' picker__chip--gone' : ''}`}>
          <span>{KIND_LABELS[value.kind]}: {value.label ?? 'nicht mehr vorhanden'}</span>
          <button type="button" className="picker__remove" onClick={() => onChange(null)} aria-label={`${label} entfernen`}>×</button>
        </span>
      </div>
    );
  }

  const choose = (t: DocumentTarget) => {
    onChange({ kind: t.kind, id: t.id, label: t.label });
    setText('');
    setDebounced('');
    setActive(0);
  };

  // Treffer nach Art gruppiert, in der Reihenfolge der ersten Fundstelle
  const groups: { kind: string; items: DocumentTarget[] }[] = [];
  for (const r of results) {
    const g = groups.find((x) => x.kind === r.kind);
    if (g) g.items.push(r);
    else groups.push({ kind: r.kind, items: [r] });
  }
  const flat = groups.flatMap((g) => g.items);

  return (
    <div className="picker__box">
      <input
        className="input"
        role="combobox"
        aria-label={label}
        aria-expanded={open && debounced.length >= 2}
        aria-controls={`${uid}-list`}
        aria-activedescendant={flat[active] ? `${uid}-${flat[active]!.kind}-${flat[active]!.id}` : undefined}
        value={text}
        placeholder={role === 'owner' ? 'Kontakt, Debitor, Kreditor oder Mitarbeiter suchen …' : 'Auftrag, Angebot, Anfrage oder Aufgabe suchen …'}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(flat.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && open && flat[active]) {
            e.preventDefault();
            choose(flat[active]!);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && debounced.length >= 2 && (
        <ul id={`${uid}-list`} className="picker__list" role="listbox">
          {found.isLoading && <li className="picker__empty">Wird gesucht …</li>}
          {found.data && results.length === 0 && <li className="picker__empty">Nichts gefunden</li>}
          {groups.map((g) => (
            <li key={g.kind} role="presentation">
              <div className="picker__group muted small" aria-hidden="true">{KIND_LABELS[g.kind]}</div>
              <ul className="picker__sub" role="group" aria-label={KIND_LABELS[g.kind]}>
                {g.items.map((t) => {
                  const isActive = flat[active]?.kind === t.kind && flat[active]?.id === t.id;
                  return (
                    <li
                      key={`${t.kind}-${t.id}`}
                      id={`${uid}-${t.kind}-${t.id}`}
                      role="option"
                      aria-selected={isActive}
                      className={`picker__option${isActive ? ' is-active' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        choose(t);
                      }}
                    >
                      {t.label}
                      {t.detail && <span className="muted small"> {t.detail}</span>}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {debounced.length < 2 && open && text.length > 0 && <small className="muted">Mindestens zwei Zeichen eingeben.</small>}
      {hint && <small className="muted">{hint}</small>}
    </div>
  );
}
